import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { postDefinitions } from "@/data/posts";
import Reveal from "@/components/Reveal";
import { useLocale } from "@/contexts/LocaleContext";
import { EditableT, EditableTLink } from "@/cms/EditableT";
import { Editable } from "@/cms/Editable";

const DeferredSuiteImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
}> = ({ src, alt, className }) => {
  const ref = useRef<HTMLImageElement | null>(null);
  const [activeSrc, setActiveSrc] = useState("");

  useEffect(() => {
    setActiveSrc("");
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      const timer = globalThis.setTimeout(() => setActiveSrc(src), 1200);
      return () => globalThis.clearTimeout(timer);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setActiveSrc(src);
        observer.disconnect();
      },
      { rootMargin: "250px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [src]);

  return (
    <img
      ref={ref}
      src={activeSrc || undefined}
      alt={alt}
      loading="lazy"
      className={className}
      width={1600}
      height={1067}
      decoding="async"
    />
  );
};

const SuitesGridSection: React.FC = () => {
  const { t } = useLocale();
  const suites = postDefinitions.filter((p) => p.categories.includes("Suites"));

  return (
    <section
      aria-label={t("home.suites_grid.headline")}
      className="relative w-full bg-white text-[#383637]"
    >
      <div className="mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 py-16 md:py-24 lg:py-32">
        {/* Heading */}
        <Reveal className="flex flex-col items-center text-center gap-[35px] max-w-[648px] mx-auto">
          <span className="block font-['Kumbh_Sans'] text-[15px] md:text-[19px] font-normal uppercase leading-[25px] tracking-[0.25em] text-[#383637] whitespace-pre-line">
            <EditableT k="home.suites_grid.kicker" />
          </span>
          <h2 className="font-['Kumbh_Sans'] text-[28px] md:text-[36px] lg:text-[45px] font-extralight uppercase leading-[1.1] tracking-[0.05em] text-[#383637] max-w-[420px] xl:max-w-none xl:whitespace-nowrap [text-wrap:balance]">
            <EditableT k="home.suites_grid.headline" multiline />
          </h2>
          <p className="font-['Baskervville'] text-[17px] font-normal leading-[28px] tracking-[0.02em] text-[#383637]">
            <EditableT k="home.suites_grid.body" multiline />
          </p>
          <EditableTLink
            k="home.suites_grid.cta_all"
            href="/suites"
            className="inline-flex items-center justify-center h-[37px] px-[30px] rounded-full bg-[#6C184F] leading-none hover:bg-[#581340] transition-colors text-[#F2F2F2] font-['Kumbh_Sans'] text-[14px] font-medium uppercase tracking-[0.08em]"
          />
        </Reveal>

        {/* Suite cards grid – <md gestapelt, ab md zwei Spalten mit Versatz */}
        <div className="mt-12 sm:mt-16 lg:mt-[156px] flex flex-col md:flex-row gap-[14px] sm:gap-[20px] md:gap-[30px] items-stretch md:items-start">
          {[0, 1].map((col) => (
            <div
              key={col}
              className={`flex-1 flex flex-col gap-[14px] sm:gap-[20px] md:gap-[30px] ${
                col === 1 ? "md:mt-[140px]" : ""
              }`}
            >
              {suites
                .filter((_, idx) => idx % 2 === col)
                .map((suite, idx) => {
                  const acf = suite.acfFields as Record<string, any>;
                  const img =
                    acf.suite_intro_image || acf.suite_gallery?.[0]?.image_url;
                  const personen =
                    acf.suite_personen_min && acf.suite_personen_max
                      ? `${acf.suite_personen_min} – ${acf.suite_personen_max} ${t("home.suites_grid.persons_suffix")}`
                      : "";
                  const groesse = acf.suite_groesse_qm
                    ? `${acf.suite_groesse_qm} m²`
                    : "";


                  return (
                    <Reveal
                      key={suite.slug}
                      delay={idx * 80}
                      className="group relative overflow-hidden aspect-[494/546] hover-lift"
                    >
                      <article className="absolute inset-0">
                        {img && (
                          <DeferredSuiteImage
                            src={img}
                            alt={acf.suite_intro_image_alt || suite.title}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.08]"
                          />
                        )}
                        <div
                          aria-hidden="true"
                          className="absolute inset-0"
                          style={{
                            background:
                              "linear-gradient(180deg, rgba(56,54,55,0.20) 0%, rgba(56,54,55,0.75) 100%)",
                          }}
                        />

                        <div className="relative h-full flex flex-col justify-between p-4 sm:p-6 md:p-7 lg:px-[28px] lg:py-[43px] text-[#F2F2F2]">
                          {/* Top row */}
                          <div className="flex items-start justify-between gap-2 sm:gap-4">
                            {personen && (
                              <span className="font-['Kumbh_Sans'] text-[11px] sm:text-[14px] md:text-[18px] lg:text-[20px] font-normal uppercase tracking-[0.2em] sm:tracking-[0.25em] backdrop-blur-[5px]">
                                <Editable id={`suite.${suite.slug}.personen`} as="span" defaultValue={personen} scope="global">{personen}</Editable>
                              </span>
                            )}
                            {groesse && (
                              <span className="font-['Kumbh_Sans'] text-[11px] sm:text-[14px] md:text-[18px] lg:text-[20px] font-normal uppercase tracking-[0.2em] sm:tracking-[0.25em] backdrop-blur-[5px] text-right whitespace-nowrap">
                                <Editable id={`suite.${suite.slug}.groesse`} as="span" defaultValue={groesse} scope="global">{groesse}</Editable>
                              </span>
                            )}
                          </div>

                          {/* Bottom block */}
                          <div className="flex flex-col items-center gap-[14px] sm:gap-[18px] lg:gap-[23px]">
                            <h3
                              className="font-['Kumbh_Sans'] text-[28px] md:text-[32px] font-normal uppercase tracking-[0.1em] leading-tight text-center text-white whitespace-pre-line"
                              style={{ textShadow: "0 3px 3px rgba(0,0,0,0.20)" }}
                            >
                              {suite.title.replace(/\s+Suite$/, " Suite").replace(" Suite", "\nSuite")}
                            </h3>
                            <a
                              href={`/suites/${suite.slug}`}
                              className="group/details inline-flex items-center gap-[5px] font-['Kumbh_Sans'] text-[14px] font-medium uppercase tracking-[0.08em] text-[#F2F2F2] hover:opacity-80 transition-opacity"
                            >
                              {t("home.suites_grid.details")}
                              <svg
                                width="14"
                                height="6"
                                viewBox="0 0 14 6"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="shrink-0 overflow-visible transition-transform duration-300 group-hover/details:translate-x-1"
                              >
                                <path
                                  d="M0 3H14M11.4872 6L14 3L11.4872 0"
                                  stroke="#F2F2F2"
                                />
                              </svg>
                            </a>
                            <div className="flex flex-nowrap justify-center items-start gap-[10px]">
                              <Link
                                to="/anfrage"
                                className="inline-flex items-center justify-center h-[37px] px-[20px] sm:px-[30px] rounded-full bg-[#A59FA1] leading-none hover:bg-[#938d8f] transition-colors text-[#F2F2F2] font-['Kumbh_Sans'] text-[14px] font-medium uppercase tracking-[0.08em] whitespace-nowrap"
                              >
                                {t("home.suites_grid.inquire")}
                              </Link>
                              <a
                                href="/buchen"
                                className="inline-flex items-center justify-center h-[37px] px-[20px] sm:px-[30px] rounded-full bg-[#6C184F] leading-none hover:bg-[#581340] transition-colors text-[#F2F2F2] font-['Kumbh_Sans'] text-[14px] font-medium uppercase tracking-[0.08em] lg:min-w-[149px] whitespace-nowrap"
                              >
                                {t("home.suites_grid.book")}
                              </a>
                            </div>
                          </div>
                        </div>
                      </article>
                    </Reveal>
                  );
                })}
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default SuitesGridSection;
